import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Briefcase, MapPin, Building2, TrendingUp, ExternalLink, Loader2,
  CheckCircle, Clock, DollarSign, Users, Target, ArrowLeft, Send,
  Globe, Sparkles, Search, Filter, ChevronDown, Star, Heart, X,
  Smartphone, Lock
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate initials from name
const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Format salary
const formatSalary = (min, max, currency = 'INR') => {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    maximumFractionDigits: 0
  });
  
  if (min && max) {
    return `${formatter.format(min)} - ${formatter.format(max)}`;
  } else if (min) {
    return `${formatter.format(min)}+`;
  } else if (max) {
    return `Up to ${formatter.format(max)}`;
  }
  return 'Not disclosed';
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
};

export default function CandidatePublicJobsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [searchStatus, setSearchStatus] = useState(null);
  const [error, setError] = useState(null);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [scoreFilter, setScoreFilter] = useState('all');
  
  // Application modal
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyingJob, setApplyingJob] = useState(null);
  const [coverNote, setCoverNote] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);
  
  // Claim profile modal
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimPhone, setClaimPhone] = useState('');
  const [claimOtp, setClaimOtp] = useState('');
  const [claimStep, setClaimStep] = useState('phone');
  const [claimLoading, setClaimLoading] = useState(false);
  
  // Auth state
  const [isOwner, setIsOwner] = useState(false);

  // Load candidate profile and job matches
  const loadData = useCallback(async () => {
    if (!slug) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // First get candidate profile
      const profileRes = await fetch(`${API_URL}/api/public/profile/${slug}`);
      if (!profileRes.ok) {
        const errData = await profileRes.json().catch(() => ({}));
        throw new Error(errData.detail || 'Profile not found');
      }
      const profileData = await profileRes.json();
      setCandidate(profileData.candidate);
      
      // Check if current user is the owner
      const token = localStorage.getItem('candidate_token');
      let isOwnerCheck = false;
      if (token) {
        try {
          const meRes = await fetch(`${API_URL}/api/candidate/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (meRes.ok) {
            const meData = await meRes.json();
            if (meData.id === profileData.candidate.id) {
              isOwnerCheck = true;
              setIsOwner(true);
            }
          }
        } catch (e) {
          console.log('Not logged in as owner');
        }
      }
      
      // Get job matches using the portal slug
      const portalSlug = profileData.candidate.portal_slug || profileData.candidate.id;
      const jobsRes = await fetch(`${API_URL}/api/portal/jobs/${portalSlug}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (jobsRes.ok) {
        const jobsData = await jobsRes.json();
        setJobs(jobsData.results || []);
        setSearchStatus(jobsData.search_status);
      } else {
        // No jobs found - that's ok, show empty state
        setJobs([]);
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle apply to job
  const handleApply = async () => {
    if (!applyingJob) return;
    
    setApplyLoading(true);
    try {
      const token = localStorage.getItem('candidate_token');
      const portalSlug = candidate.portal_slug || candidate.id;
      
      const res = await fetch(`${API_URL}/api/portal/jobs/${portalSlug}/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          result_id: applyingJob.id,
          cover_note: coverNote
        })
      });
      
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to apply');
      }
      
      toast.success('Application sent successfully!');
      setShowApplyModal(false);
      setCoverNote('');
      setApplyingJob(null);
      
      // Update local state
      setJobs(prev => prev.map(j => 
        j.id === applyingJob.id ? { ...j, applied: true, applied_at: new Date().toISOString() } : j
      ));
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setApplyLoading(false);
    }
  };

  // Handle claim profile - send OTP
  const handleSendClaimOtp = async () => {
    if (!claimPhone || claimPhone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setClaimLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/public/profile/${slug}/claim/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: claimPhone })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send OTP');
      }
      
      toast.success('OTP sent to your WhatsApp!');
      setClaimStep('otp');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClaimLoading(false);
    }
  };

  // Handle claim profile - verify OTP
  const handleVerifyClaimOtp = async () => {
    if (!claimOtp || claimOtp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    
    setClaimLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/public/profile/${slug}/claim/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: claimPhone, otp: claimOtp })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Invalid OTP');
      }
      
      // Store token
      localStorage.setItem('candidate_token', data.access_token);
      localStorage.setItem('candidate_user', JSON.stringify(data.user));
      
      toast.success('Profile claimed successfully!');
      setShowClaimModal(false);
      setIsOwner(true);
      
      // Reload to get full data
      loadData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClaimLoading(false);
    }
  };

  // Filter jobs
  const filteredJobs = jobs.filter(job => {
    const matchesSearch = !searchTerm || 
      (job.job_title?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (job.company_name?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesScore = scoreFilter === 'all' ||
      (scoreFilter === 'high' && job.match_score >= 80) ||
      (scoreFilter === 'medium' && job.match_score >= 60 && job.match_score < 80) ||
      (scoreFilter === 'low' && job.match_score < 60);
    
    return matchesSearch && matchesScore;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading job matches...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8">
          <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Jobs Not Found</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline">
            Go Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/images/daruwallas-logo.png" 
              alt="Daruwallas" 
              className="h-8 object-contain"
            />
          </div>
          
          <div className="flex items-center gap-3">
            {!isOwner && (
              <Button
                onClick={() => setShowClaimModal(true)}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                size="sm"
              >
                <Smartphone className="w-4 h-4 mr-2" />
                Claim Profile
              </Button>
            )}
            {isOwner && (
              <Button
                onClick={() => navigate('/my')}
                variant="outline"
                size="sm"
              >
                My Portal
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Candidate Info Header */}
        <div className="mb-8">
          <Link to={`/c/${slug}`} className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Profile
          </Link>
          
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16 border-2 border-white shadow-lg">
              <AvatarImage src={candidate?.profile_picture?.startsWith('data:') || candidate?.profile_picture?.startsWith('http') ? candidate.profile_picture : (candidate?.profile_picture ? `${API_URL}${candidate.profile_picture}` : null)} />
              <AvatarFallback className="bg-gradient-to-br from-purple-600 to-indigo-600 text-white text-xl font-bold">
                {getInitials(candidate?.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                Job Matches for {candidate?.name}
              </h1>
              <p className="text-slate-600">
                {jobs.length} opportunities found based on your profile
              </p>
            </div>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search jobs or companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={scoreFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setScoreFilter('all')}
                size="sm"
              >
                All
              </Button>
              <Button
                variant={scoreFilter === 'high' ? 'default' : 'outline'}
                onClick={() => setScoreFilter('high')}
                size="sm"
                className={scoreFilter === 'high' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                80%+ Match
              </Button>
              <Button
                variant={scoreFilter === 'medium' ? 'default' : 'outline'}
                onClick={() => setScoreFilter('medium')}
                size="sm"
                className={scoreFilter === 'medium' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              >
                60-80%
              </Button>
            </div>
          </div>
        </div>

        {/* Job Cards */}
        {filteredJobs.length === 0 ? (
          <Card className="p-12 text-center">
            <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-800 mb-2">No Jobs Found</h3>
            <p className="text-slate-600 mb-4">
              {searchTerm || scoreFilter !== 'all' 
                ? 'Try adjusting your filters' 
                : 'We haven\'t found any job matches yet. Check back soon!'}
            </p>
            {searchStatus === 'processing' && (
              <div className="flex items-center justify-center gap-2 text-purple-600">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>AI is still searching for jobs...</span>
              </div>
            )}
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <Card 
                key={job.id} 
                className={`overflow-hidden hover:shadow-lg transition-all border-l-4 ${
                  job.applied 
                    ? 'border-l-green-500 bg-green-50/30' 
                    : job.match_score >= 80 
                      ? 'border-l-green-500' 
                      : job.match_score >= 60 
                        ? 'border-l-amber-500' 
                        : 'border-l-slate-300'
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    {/* Company Logo */}
                    <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      {job.company_logo ? (
                        <img src={job.company_logo} alt="" className="w-10 h-10 object-contain" />
                      ) : (
                        <Building2 className="w-7 h-7 text-slate-400" />
                      )}
                    </div>
                    
                    {/* Job Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-800 hover:text-purple-600 cursor-pointer">
                            {job.job_title || 'Untitled Position'}
                          </h3>
                          <p className="text-slate-600">{job.company_name || 'Company'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${
                            job.match_score >= 80 ? 'bg-green-100 text-green-700' :
                            job.match_score >= 60 ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          } text-sm px-3 py-1`}>
                            <Target className="w-3 h-3 mr-1" />
                            {job.match_score || 0}% Match
                          </Badge>
                          {job.applied && (
                            <Badge className="bg-green-500 text-white">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Applied
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Job Meta */}
                      <div className="flex flex-wrap gap-3 text-sm text-slate-500 mb-3">
                        {job.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.location}
                          </span>
                        )}
                        {(job.salary || job.salary_min || job.salary_max) && (
                          <span className="flex items-center gap-1 text-green-600 font-medium">
                            <DollarSign className="w-4 h-4" />
                            {job.salary || formatSalary(job.salary_min, job.salary_max)}
                          </span>
                        )}
                        {job.experience_required && (
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            {job.experience_required}
                          </span>
                        )}
                        {job.job_type && (
                          <Badge variant="outline" className="text-xs">
                            {job.job_type}
                          </Badge>
                        )}
                        {job.work_from_home && (
                          <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
                            Remote/WFH
                          </Badge>
                        )}
                        {(job.posted_at || job.found_at) && (
                          <span className="flex items-center gap-1 text-slate-400">
                            <Clock className="w-4 h-4" />
                            {job.posted_at || formatDate(job.found_at)}
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      {job.description && (
                        <p className="text-sm text-slate-600 line-clamp-3 mb-3">{job.description}</p>
                      )}

                      {/* Requirements & Benefits */}
                      {job.requirements?.length > 0 && (
                        <div className="mb-2">
                          <span className="text-xs font-medium text-slate-500">Requirements: </span>
                          <span className="text-xs text-slate-600">
                            {job.requirements.slice(0, 3).join(' | ')}
                            {job.requirements.length > 3 && ` +${job.requirements.length - 3} more`}
                          </span>
                        </div>
                      )}
                      {job.benefits?.length > 0 && (
                        <div className="mb-3">
                          <span className="text-xs font-medium text-emerald-600">Benefits: </span>
                          <span className="text-xs text-slate-600">
                            {job.benefits.slice(0, 3).join(' | ')}
                          </span>
                        </div>
                      )}
                      
                      {/* Match Reasons */}
                      {job.match_reasons && job.match_reasons.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs font-medium text-slate-500 mb-2">Why you match:</p>
                          <div className="flex flex-wrap gap-2">
                            {job.match_reasons.slice(0, 4).map((reason, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                <Sparkles className="w-3 h-3 mr-1" />
                                {reason}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex flex-wrap gap-2">
                        {!job.applied ? (
                          <Button
                            onClick={() => {
                              setApplyingJob(job);
                              setShowApplyModal(true);
                            }}
                            className="bg-purple-600 hover:bg-purple-700"
                            size="sm"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Express Interest
                          </Button>
                        ) : (
                          <Button disabled variant="outline" size="sm" className="text-green-600">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Applied {job.applied_at && formatDate(job.applied_at)}
                          </Button>
                        )}
                        {job.source_url && (
                          <Button
                            onClick={() => window.open(job.source_url, '_blank')}
                            variant="outline"
                            size="sm"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View Original
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Search Status */}
        {searchStatus === 'processing' && jobs.length > 0 && (
          <div className="mt-6 p-4 bg-purple-50 rounded-xl border border-purple-200 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
            <span className="text-purple-700">AI is still searching for more jobs. Check back for updates!</span>
          </div>
        )}
      </main>

      {/* Apply Modal */}
      {showApplyModal && applyingJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Express Interest</h2>
                  <p className="text-slate-600">{applyingJob.job_title} at {applyingJob.company_name}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowApplyModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Cover Note (Optional)
                </label>
                <Textarea
                  value={coverNote}
                  onChange={(e) => setCoverNote(e.target.value)}
                  placeholder="Add a brief note about why you're interested in this role..."
                  rows={4}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleApply}
                  disabled={applyLoading}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {applyLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Interest
                </Button>
                <Button
                  onClick={() => setShowApplyModal(false)}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Claim Profile Modal */}
      {showClaimModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-2">Claim Your Profile</h2>
              <p className="text-slate-600 mb-6">
                Verify your phone number via WhatsApp OTP to claim this profile
              </p>

              {claimStep === 'phone' ? (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      value={claimPhone}
                      onChange={(e) => setClaimPhone(e.target.value)}
                      placeholder="Enter your phone number"
                      className="text-lg"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Must match the phone number on this profile
                    </p>
                  </div>
                  <Button
                    onClick={handleSendClaimOtp}
                    disabled={claimLoading}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {claimLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Smartphone className="w-4 h-4 mr-2" />
                    )}
                    Send OTP via WhatsApp
                  </Button>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Enter OTP
                    </label>
                    <Input
                      type="text"
                      value={claimOtp}
                      onChange={(e) => setClaimOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6-digit OTP"
                      className="text-2xl text-center tracking-widest"
                      maxLength={6}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Check your WhatsApp for the OTP
                    </p>
                  </div>
                  <Button
                    onClick={handleVerifyClaimOtp}
                    disabled={claimLoading || claimOtp.length !== 6}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {claimLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Verify & Claim
                  </Button>
                  <Button
                    onClick={() => setClaimStep('phone')}
                    variant="ghost"
                    className="w-full mt-2"
                  >
                    Change Phone Number
                  </Button>
                </>
              )}

              <Button
                onClick={() => {
                  setShowClaimModal(false);
                  setClaimStep('phone');
                  setClaimOtp('');
                }}
                variant="ghost"
                className="w-full mt-4"
              >
                Cancel
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
